import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useMutation } from "react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { clearFlaggedQuestion } from "../../../../features/candidate-exam/candidate-exam-slice";
import { exams } from "../../../../api/exams";
import Modal from "../../survey/components/Modal";
import DemographicsForm from "./Demographics";
import Loader from "../../../../components/loader/Loader";

// Shared by SurveyCard (card grid) and SurveyRow (self-review list) so both
// start a survey, gate on demographics and view responses the same way.
const useSurveyActions = ({ survey, refetch }: { survey: any, refetch: any }) => {
  const navigate = useNavigate()
  const { t } = useTranslation();
  const dispatch = useDispatch()
  const location = useLocation();
  const currentLanguage = useSelector((state: any) => state.traslation.language);
  const [openModal, setOpenModal] = React.useState<boolean>(false);
  const user = JSON.parse(sessionStorage.getItem('user') || '{}')
  const isReview = location.pathname.includes("/candidate/my-review");

  const startSurvey = (data: any) => {
    const questionResponse = data?.data?.questions
    sessionStorage.setItem('survey-questions', JSON.stringify(questionResponse))
    sessionStorage.setItem('survey-user', 'employee')
    navigate('/candidate/survey')
  }

  // Without this a failed question fetch did nothing visible. 4xx messages
  // are written for the participant (e.g. a culture audit random section
  // short of active questions names the section); 5xx ones aren't.
  const onStartSurveyError = (error: any) => {
    const status = error?.response?.status;
    const message = error?.response?.data?.message;
    toast.error(status && status < 500 && message ? message : t('survey-start-failed'), { duration: 8000 });
    // Locked by the facilitator since the list loaded: refresh so the row shows it.
    if (error?.response?.data?.code === 'assessment_locked') refetch?.();
  }

  const getQuestionsmutation = useMutation(exams.getExamQuestionsv2, {
    onSuccess: async (data: any) => {
      startSurvey(data)
    },
    onError: onStartSurveyError
  })

  const getReviewerQuestionsmutation = useMutation(exams.getReviewerExamQuestionsv2, {
    onSuccess: async (data: any) => {
      startSurvey(data)
    },
    onError: onStartSurveyError
  })

  // Fetching a survey's questions can take a few seconds on a slow
  // connection; until it returns the page looked unchanged, so people clicked
  // again. The overlay also blocks the other cards until we navigate away.
  const isLoadingQuestions = getQuestionsmutation.isLoading || getReviewerQuestionsmutation.isLoading;

  const takeSurvey = (exam: any) => {
    if (isLoadingQuestions) return;
    if (exam?.requireDemographics && !exam?.hasFilledDemographics) {
      setOpenModal(true)
      return
    }
    dispatch(clearFlaggedQuestion(''));
    window.sessionStorage.setItem("assessment-type", isReview ? "review" : "survey");
    window.sessionStorage.setItem("active-assessment-id", exam?.user_exam_id);
    window.sessionStorage.setItem("active-assessment-user-id", exam?.user_id);
    window.sessionStorage.setItem("active-assessment", JSON.stringify(exam));
    // Culture audit only: one UserExam can span several assessments, so the
    // specific assessment being taken must be carried through to Employee.tsx
    // for save/submit. 360/appraisal surveys don't set exam?.assessment?.id
    // here (undefined), so sessionStorage holds "" and every downstream call
    // keeps omitting assessmentId, same as before this change.
    window.sessionStorage.setItem("active-assessment-assessment-id", exam?.assessment?.id || "");
    if (isReview) {
      getReviewerQuestionsmutation.mutate({
        reviewerId: user?.user?.user_id || window.sessionStorage.getItem("user_id"),
        userId: exam?.user_id,
        userExamId: exam?.user_exam_id,
        language: currentLanguage,
      });
    }
    else {
      getQuestionsmutation.mutate({
        userId: user?.user?.user_id || window.sessionStorage.getItem("user_id"),
        examId: exam?.user_exam_id,
        userExamId: exam?.user_id,
        assessmentId: exam?.assessment?.id,
        showOnlyQuestions: true,
        language: currentLanguage,
      } as any);
    }
  }

  const viewResponse = (survey: any) => {
    sessionStorage.setItem('activeSurvey', JSON.stringify(survey))
    navigate(`/candidate/view-response`)
  }

  // The loading overlay and demographics modal; render once per card/row.
  const overlays = (
    <>
      {isLoadingQuestions && (
        <div
          role="status"
          aria-live="polite"
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white/90"
        >
          <div className="h-24 overflow-hidden flex items-center">
            <Loader />
          </div>
          <p className="mt-2 text-gray-600 text-base">{t("loading-survey")}</p>
        </div>
      )}
      {openModal &&
        <Modal
          closeModal={(value: any) => setOpenModal(value)}
          title=""
        >
          <div className="flex -mt-8 items-center gap-2">
            <DemographicsForm setOpen={setOpenModal} refetch={refetch} survey={survey} />
          </div>
        </Modal>
      }
    </>
  );

  return { takeSurvey, viewResponse, isLoadingQuestions, isReview, overlays };
};

export default useSurveyActions;
